import { GirafeHTMLElement } from '@geogirafe/lib-geoportal/base';
import { Map } from 'ol';
import Draw from 'ol/interaction/Draw';
import { Polygon } from 'ol/geom';
import { Fill, Stroke, Style } from 'ol/style.js';
import { DrawEvent } from 'ol/interaction/Draw';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import WFS from 'ol/format/WFS';
import * as olFormatFilter from 'ol/format/filter';
import Feature from 'ol/Feature';

import BsPrintingService from '../../bsprinting/bsprintingservice';
import { Service } from '../service/service';
import { KleinbautenAusschnittState, KleinbautenReportConfig } from '../../../bs/types';

class KleinbautenAusschnittComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../../../node_modules/@geogirafe/lib-geoportal/styles/common.css', '../item-style.css'];

  private readonly templateName: string = 'kleinbautenriehen_reports';
  service!: Service;

  private olMap: Map | null = null;
  private drawInteraction: Draw | null = null;
  private vectorSource: VectorSource | null = null;
  private vectorLayer: VectorLayer<VectorSource> | null = null;

  private printService!: BsPrintingService;

  private get reportState(): KleinbautenAusschnittState {
    return this.state.extendedState[this.templateName] as KleinbautenAusschnittState;
  }

  constructor() {
    super('kleinbauten-ausschnitt-component');
  }

  private initialize() {
    if (!this.state.extendedState[this.templateName]) {
      this.state.extendedState[this.templateName] = {
        currentState: 'inactive'
      };
    }

    this.service = new Service(this.context);
    this.printService = new BsPrintingService(this.context);
  }

  get config() {
    return this.context.configManager.Config;
  }

  get config_liegenschaft() {
    return this.context.configManager.Config!.extendedConfig!.kleinbautenReport as KleinbautenReportConfig;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.initialize();

    this.olMap = this.context.mapManager.getMap();

    this.vectorSource = new VectorSource();
    this.vectorLayer = new VectorLayer({
      source: this.vectorSource
    });
    this.olMap?.addLayer(this.vectorLayer);

    this.initDrawInteraction();
    super.render();
  }

  override disconnectedCallback() {
    this.removeMapInteraction();
    if (this.vectorLayer && this.olMap) {
      this.olMap.removeLayer(this.vectorLayer);
    }
    super.disconnectedCallback();
  }

  private updateCursorStyle(state: 'active' | 'inactive'): void {
    if (!this.olMap) return;
    const targetElem = this.olMap.getTargetElement();
    if (!targetElem) return;
    targetElem.style.cursor = state === 'active' ? 'crosshair' : 'default';
  }

  private initDrawInteraction() {
    if (!this.olMap) return;

    this.drawInteraction = new Draw({
      source: this.vectorSource!,
      type: 'Polygon',
      style: new Style({
        stroke: new Stroke({
          color: this.config.drawing.defaultStrokeColor,
          width: 2
        }),
        fill: new Fill({
          color: this.config.drawing.defaultFillColor
        })
      })
    });
    this.drawInteraction.on('drawend', async (evt: DrawEvent) => {
      this.setState('processing');

      const geom = evt.feature.getGeometry() as Polygon;

      try {
        const features = await this.getFeatureByPolygon(geom, ['BS_Liegenschaften_Grundstuecke_Liegenschaftsparzelle']);

        const egrids = features
          .map((feature) => feature.get('bs_egrid'))
          .filter((egrid): egrid is string => typeof egrid === 'string' && egrid.trim().length > 0);

        if (egrids.length === 0) {
          this.reportState.errorMessage = 'Keine Parzellen gefunden';
          this.setState('error');
          return;
        }

        const kbEgrids = await this.filterKBEgrids(egrids);

        if (kbEgrids.length === 0) {
          this.reportState.errorMessage = 'Keine Parzellen mit Kleinbauten selektiert';
          this.setState('error');
          return;
        }

        const downloadUrl = await this.printService.createMultiReport('kleinbautenriehen_report', kbEgrids.join(','));

        if (!downloadUrl) {
          return;
        }

        this.reportState.downloadUrl = downloadUrl;
        this.setState('download');
      } catch (error) {
        console.error('Error:', error);
        this.reportState.errorMessage = error instanceof Error ? error.message : String(error);
        this.setState('error');
      } finally {
        this.vectorSource?.clear();
      }
    });
  }

  private async filterKBEgrids(egrids: string[]): Promise<string[]> {
    const url = `${this.context.configManager.Config.general.defaultUrlPrefix}/filter_kb_egrids/${egrids.join(',')}`;

    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`filterKBEgrids failed: ${resp.status} - ${resp.statusText}`);
      }
      const data = await resp.json();
      return data as string[];
    } catch (error) {
      console.error('filterKBEgrids error', error);
      throw error;
    }
  }

  private addMapInteraction() {
    this.registerInteractionListener('map.draw', true);
    if (this.olMap && this.drawInteraction) {
      this.olMap.addInteraction(this.drawInteraction);
    }
  }

  private removeMapInteraction() {
    this.unregisterInteractionListeners('map.draw');
    if (this.olMap && this.drawInteraction) {
      this.olMap.removeInteraction(this.drawInteraction);
    }
  }

  protected onReportClick() {
    if (this.reportState.currentState === 'inactive') {
      this.setState('active');
    } else if (this.reportState.currentState === 'active') {
      this.setState('inactive');
    } else if (this.reportState.currentState === 'download') {
      window.open(this.reportState.downloadUrl);
      this.setState('inactive');
    } else if (this.reportState.currentState === 'error') {
      this.reportState.currentState = 'inactive';
      this.setState('inactive');
    }
  }

  private setState(newState: KleinbautenAusschnittState['currentState']) {
    this.reportState.currentState = newState;
    switch (newState) {
      case 'inactive':
        this.removeMapInteraction();
        this.updateCursorStyle('inactive');
        break;
      case 'active':
        this.addMapInteraction();
        this.updateCursorStyle('active');
        break;
      case 'processing':
        this.removeMapInteraction();
        this.updateCursorStyle('inactive');
        break;
      case 'download':
      case 'error':
        this.removeMapInteraction();
        this.updateCursorStyle('inactive');
        break;
    }
    this.render();
  }

  async getFeatureByPolygon(geom: Polygon, featureList: string[]): Promise<Feature[]> {
    const wfsFormat = new WFS();
    const featureRequest = wfsFormat.writeGetFeature({
      featurePrefix: this.config_liegenschaft!.featurePrefix,
      featureNS: this.config_liegenschaft!.featureNS,
      featureTypes: featureList,
      filter: olFormatFilter.intersects('the_geom', geom, this.config_liegenschaft!.srsName),
      srsName: this.config_liegenschaft!.srsName
    });

    const requestBody = new XMLSerializer().serializeToString(featureRequest);
    const wfsUrl = `${this.config_liegenschaft!.featureNS}/mapserv_proxy?ogcserver=${this.config_liegenschaft.ogcserver}`;

    try {
      const resp = await fetch(wfsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/xml; charset=UTF-8' },
        body: requestBody,
        credentials: 'include'
      });
      if (!resp.ok) {
        throw new Error(`WFS request failed: ${resp.status} ${resp.statusText}`);
      }

      const textData = await resp.text();
      const features = wfsFormat.readFeatures(textData);
      return features as Feature[];
    } catch (error) {
      console.error('Error in getFeatureByPolygon:', error);
      throw error;
    }
  }
}

export default KleinbautenAusschnittComponent;
